  import React, { useEffect } from "react";
  import { useLocation } from "react-router-dom";
  import Sidebar from "./Components/Common/Sidebar";
  import { useTheme } from "./ThemeContext";

  const Layout = ({ children }) => {
    const location = useLocation();
    const currentPath = location.pathname.toLowerCase();

    const hideSidebarRoutes = [
      "/login",
      "/signup",
      "/workspace",
      "/workspace-company",
      "/workspace-invite",
      "/oauth/callback",
      "/share-chat",
      "/forgot-password"
    ];

    const shouldHideSidebar = hideSidebarRoutes.some(route =>
      currentPath === route.toLowerCase() || currentPath.startsWith(route.toLowerCase() + '/')
    );

    const getPageHeaderInfo = (path) => {
      const routes = {
        "/home": { title: "Home", subtitle: "Pre-configured AI agents for specific business functions." },
        "/dashboard": { title: "Dashboard", subtitle: "Overview of your agents and activity." },
        "/screeningagent": { title: "Agent", subtitle: "AI-powered Agent." },
        "/myagents": { title: "My Agents", subtitle: "Manage and monitor your AI agents." },
        "/chatinterface": { title: "Chat Interface", subtitle: "Interact with your agents directly." },
        "/sharingagent": { title: "Sharing Agent", subtitle: "Securely share agent access." },
        "/logs": { title: "Logs", subtitle: "System and agent activity logs." },
        "/artifacts": { title: "Artifacts", subtitle: "View generated artifacts." },
        "/analytics": { title: "Analytics", subtitle: "Usage and performance analytics." },
        "/kb": { title: "Knowledge Base", subtitle: "Manage agent knowledge sources." },
        "/settings": { title: "Settings", subtitle: "Profile settings and subscription management." },
        "/profilemanagement": { title: "Profile Management", subtitle: "Manage your profile." },
        "/userrole": { title: "User Role", subtitle: "Manage user roles." },
        "/notification": { title: "Notification", subtitle: "Manage notifications." },
        "/subscription": { title: "Subscription", subtitle: "Manage subscription." },
        "/workspacemanagement": { title: "Workspace Management", subtitle: "Manage workspaces." },
        "/create-agent": { title: "Create Agent", subtitle: "Create a new custom agent." },
        "/chat-bot": { title: "Generator", subtitle: "AI Generator." },
      };
      if (path.startsWith("/chatinterface/")) {
        return { title: "Chat Interface", subtitle: "Interact with your agents directly." };
      }
      return routes[path] || { title: "DMS", subtitle: "Document Management System" };
    };

    const { title, subtitle } = getPageHeaderInfo(currentPath);

    const { theme, toggleTheme } = useTheme();

    useEffect(() => {
      document.body.setAttribute('data-theme', theme);
    }, [theme]);

    const themeStyles = `
      :root {
        --bg-main: #f4f6f8;
        --bg-surface: #ffffff;
        --text-main: #212529;
        --text-secondary: #6c757d;
        --border-color: #dee2e6;
        --card-bg: #ffffff;
        --input-bg: #ffffff;
        --input-border: #ced4da;
        --table-hover-bg: rgba(0,0,0,0.075);
        --link-color: #0d6efd;
        --primary-color: #0d6efd;
        --danger-color: #dc3545;
        --success-color: #198754;
        --info-color: #0dcaf0;
        --warning-color: #ffc107;
        --light-gray: #e9ecef;
        --medium-gray: #ced4da;
      }

      [data-theme='dark'] {
        --bg-main: #121212;
        --bg-surface: #1e1e1e;
        --text-main: #e0e0e0;
        --text-secondary: #a0a0a0;
        --border-color: #333333;
        --card-bg: #1e1e1e;
        --input-bg: #2c2c2c;
        --input-border: #444444;
        --table-hover-bg: rgba(255,255,255,0.075);
        --link-color: #6ea8fe;
        --primary-color: #3783ff;
        --danger-color: #e85a69;
        --success-color: #48a57a;
        --info-color: #41d3f5;
        --warning-color: #ffcd39;
        --light-gray: #2c2c2c;
        --medium-gray: #495057;
      }

      body {
        background-color: var(--bg-main) !important;
        color: var(--text-main) !important;
      }
      
      body, .card, .modal-content, .form-control, .btn, .app-theme-bg, .list-group-item, .alert {
        transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out;
      }

      h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .h6 {
        color: var(--text-main) !important;
      }

      .app-theme-bg {
        background-color: var(--bg-main);
        color: var(--text-main);
      }

      a { color: var(--link-color); }
      a:hover { filter: brightness(1.2); }
      hr { border-top-color: var(--border-color) !important; }

      .card {
        background-color: var(--card-bg) !important;
        color: var(--text-main) !important;
        border-color: var(--border-color) !important;
      }
      .card-body { color: var(--text-main) !important; }

      .card-header, .card-footer {
        background-color: rgba(0,0,0,0.03) !important;
        border-color: var(--border-color) !important;
      }
      [data-theme='dark'] .card-header, 
      [data-theme='dark'] .card-footer {
        background-color: rgba(255,255,255,0.03) !important;
      }

      [data-theme='dark'] .form-control,
      [data-theme='dark'] .form-select,
      [data-theme='dark'] .input-group-text {
        background-color: var(--input-bg) !important;
        border-color: var(--input-border) !important;
        color: var(--text-main) !important;
      }
      [data-theme='dark'] .form-control:focus,
      [data-theme='dark'] .form-select:focus {
        background-color: var(--input-bg) !important;
        color: var(--text-main) !important;
        border-color: #5c5c5c !important;
      }
      [data-theme='dark'] .form-control::placeholder {
        color: var(--text-secondary) !important;
      }

      [data-theme='dark'] table {
        color: var(--text-main) !important;
        border-color: var(--border-color) !important;
      }
      [data-theme='dark'] .table {
        color: var(--text-main) !important;
        --bs-table-color: var(--text-main);
        --bs-table-bg: var(--bg-surface);
        --bs-table-border-color: var(--border-color);
        --bs-table-hover-bg: var(--table-hover-bg);
        --bs-table-striped-bg: rgba(255,255,255,0.05);
        background-color: var(--bg-surface) !important;
      }
      [data-theme='dark'] .table > :not(caption) > * > * {
        background-color: transparent !important;
        color: var(--text-main) !important;
        border-color: var(--border-color) !important;
      }
      [data-theme='dark'] .table-bordered { border-color: var(--border-color) !important; }

      [data-theme='dark'] .list-group-item,
      [data-theme='dark'] .list-group-item-action {
          background-color: var(--card-bg) !important;
          color: var(--text-main) !important;
          border-color: var(--border-color) !important;
      }

      [data-theme='dark'] .modal-content,
      [data-theme='dark'] .dropdown-menu,
      [data-theme='dark'] .popover {
        background-color: var(--bg-surface) !important;
        border-color: var(--border-color) !important;
        color: var(--text-main) !important;
      }
      [data-theme='dark'] .modal-header,
      [data-theme='dark'] .modal-footer { border-color: var(--border-color) !important; }
      [data-theme='dark'] .btn-close { filter: invert(1) grayscale(100%) brightness(200%); }
      [data-theme='dark'] .dropdown-item { color: var(--text-main) !important; }
      [data-theme='dark'] .dropdown-item:hover,
      [data-theme='dark'] .dropdown-item:focus { background-color: var(--border-color) !important; }

      [data-theme='dark'] .nav-tabs .nav-link.active {
          background-color: var(--bg-surface) !important;
          color: var(--text-main) !important;
          border-color: var(--border-color) var(--border-color) var(--bg-surface) !important;
      }
      [data-theme='dark'] .nav-link { color: var(--link-color); }

      [data-theme='dark'] .page-link {
          background-color: var(--bg-surface) !important;
          border-color: var(--border-color) !important;
          color: var(--text-main) !important;
      }
      [data-theme='dark'] .page-item.disabled .page-link {
          background-color: var(--bg-main) !important;
          color: var(--text-secondary) !important;
      }

      [data-theme='dark'] .log-container, 
      [data-theme='dark'] .logs-wrapper {
          background-color: #000000 !important;
          color: #00ff00 !important;
      }
      [data-theme='dark'] pre, [data-theme='dark'] code {
          color: #e685b5; 
          background-color: rgba(255,255,255,0.05) !important;
          border-color: var(--border-color);
      }

      [data-theme='dark'] .btn-light, 
      [data-theme='dark'] .btn-white {
          background-color: var(--bg-surface) !important;
          color: var(--text-main) !important;
          border-color: var(--border-color) !important;
      }
      [data-theme='dark'] .btn-secondary {
          background-color: var(--medium-gray) !important;
          border-color: var(--medium-gray) !important;
          color: var(--bg-main) !important;
      }
      [data-theme='dark'] .btn-outline-secondary {
          color: var(--text-secondary) !important;
          border-color: var(--text-secondary) !important;
      }
      [data-theme='dark'] .btn-outline-secondary:hover {
          background-color: var(--text-secondary) !important;
          color: var(--bg-main) !important;
      }

      [data-theme='dark'] .alert-light,
      [data-theme='dark'] .alert-info {
        background-color: var(--bg-surface) !important;
        color: var(--text-main) !important;
        border-color: var(--border-color) !important;
      }
      [data-theme='dark'] .alert {
        background-color: var(--bg-surface);
        color: var(--text-main);
        border-color: var(--border-color) !important;
      }

      [data-theme='dark'] .badge.bg-light {
        background-color: var(--bg-surface) !important;
        color: var(--text-main) !important;
      }
      [data-theme='dark'] .badge.bg-success { background-color: var(--success-color) !important; color: #fff !important; }
      [data-theme='dark'] .badge.bg-danger { background-color: var(--danger-color) !important; color: #fff !important; }

      [data-theme='dark'] .progress { background-color: var(--input-bg); }
      [data-theme='dark'] .progress-bar { background-color: var(--primary-color); }
      [data-theme='dark'] .breadcrumb {
        background-color: var(--bg-surface);
        padding: 0.75rem 1rem;
        border-radius: 0.25rem;
      }
      [data-theme='dark'] .breadcrumb-item a { color: var(--link-color); }
      [data-theme='dark'] .breadcrumb-item.active { color: var(--text-secondary); }

      [data-theme='dark'] .login-card {
          background-color: var(--bg-surface) !important;
          border: 1px solid var(--border-color) !important;
          color: var(--text-main) !important;
      }
      [data-theme='dark'] .login-wrapper { background-color: var(--bg-main) !important; }

      [data-theme='dark'] .cx-chat-panel,
      [data-theme='dark'] .cx-left-panel .bg-white,
      [data-theme='dark'] .cx-right-panel .bg-white,
      [data-theme='dark'] .cx-chat-container .bg-white {
          background-color: var(--bg-surface) !important;
          color: var(--text-main) !important;
          border-color: var(--border-color) !important;
      }
      [data-theme='dark'] .cx-message-bubble.is-agent,
      [data-theme='dark'] .bot-message {
          background-color: #2d2d2d !important;
          color: var(--text-main) !important;
          border: 1px solid var(--border-color);
      }
      [data-theme='dark'] .cx-message-bubble.is-user,
      [data-theme='dark'] .user-message {
          background-color: #0d6efd !important;
          color: #ffffff !important;
      }
      
      [data-theme='dark'] .artifact-item,
      [data-theme='dark'] .file-card {
          background-color: var(--bg-surface) !important;
          border-color: var(--border-color) !important;
      }

      [data-theme='dark'] .workspace-card,
      [data-theme='dark'] .signup-card,
      [data-theme='dark'] .login-card {
          background-color: var(--bg-surface) !important;
          border-color: var(--border-color) !important;
          color: var(--text-main) !important;
      }
      [data-theme='dark'] .workspace-wrapper,
      [data-theme='dark'] .signup-wrapper,
      [data-theme='dark'] .login-wrapper { background-color: var(--bg-main) !important; }
      [data-theme='dark'] .selection-box {
          background-color: var(--bg-main) !important;
          border-color: var(--border-color) !important;
          color: var(--text-main) !important;
      }
      [data-theme='dark'] .selection-box.active {
          background-color: rgba(13, 110, 253, 0.15) !important;
          border-color: var(--link-color) !important;
      }
      [data-theme='dark'] .option-title,
      [data-theme='dark'] .signup-label,
      [data-theme='dark'] .workspace-label,
      [data-theme='dark'] .login-label { color: var(--text-main) !important; }
      [data-theme='dark'] .option-subtitle { color: var(--text-secondary) !important; }

      [data-theme='dark'] .zapier-card,
      [data-theme='dark'] .attachment-card,
      [data-theme='dark'] .chat-card {
          background-color: var(--bg-surface) !important;
          border-color: var(--border-color) !important;
          color: var(--text-main) !important;
      }

      [data-theme='dark'] .table-color-plans th,
      [data-theme='dark'] .content-background {
          background-color: var(--bg-surface) !important;
          color: var(--text-main) !important;
          border-color: var(--border-color) !important;
      }
      [data-theme='dark'] .feature-tooltip-box {
          background-color: var(--card-bg) !important;
          color: var(--text-main) !important;
          border: 1px solid var(--border-color);
      }

      [data-theme='dark'] .custom-dropdown-list {
          background-color: var(--bg-surface) !important;
          border-color: var(--border-color) !important;
      }
      [data-theme='dark'] .custom-dropdown-item { color: var(--text-main) !important; }
      [data-theme='dark'] .custom-dropdown-item:hover { background-color: var(--border-color) !important; }
      [data-theme='dark'] .custom-dropdown-header {
          background-color: var(--input-bg) !important;
          color: var(--text-main) !important;
          border-color: var(--border-color) !important;
      }

      [data-theme='dark'] .chathistory-details,
      [data-theme='dark'] .cx-doc-item {
          background-color: var(--bg-main) !important;
          color: var(--text-main) !important;
          border: 1px solid var(--border-color);
      }

      [data-theme='dark'] .email-tag {
        background-color: var(--input-bg);
        border: 1px solid var(--border-color);
        color: var(--text-main);
      }
      [data-theme='dark'] .close-tag { color: var(--text-secondary); }
      [data-theme='dark'] .divider-text {
        background-color: var(--bg-main);
        color: var(--text-secondary);
      }
      [data-theme='dark'] .social-icon,
      [data-theme='dark'] .monospear-footer img { filter: brightness(0) invert(1); }
      [data-theme='dark'] .skip-btn {
        background-color: transparent;
        color: var(--text-main);
        border: 1px solid var(--border-color);
      }
      [data-theme='dark'] .skip-btn:hover { background-color: var(--border-color); }

      [data-theme='dark'] .bg-white, 
      [data-theme='dark'] .bg-light,
      [data-theme='dark'] .bg-body,
      [data-theme='dark'] .bg-gray-100,
      [data-theme='dark'] .bg-gray-200 {
        background-color: var(--bg-surface) !important;
        color: var(--text-main) !important;
      }
      [data-theme='dark'] .text-dark,
      [data-theme='dark'] .text-body { color: var(--text-main) !important; }
      [data-theme='dark'] .text-muted { color: var(--text-secondary) !important; }
      [data-theme='dark'] .text-black { color: var(--text-main) !important; }

      [data-theme='dark'] .border, 
      [data-theme='dark'] .border-top, 
      [data-theme='dark'] .border-bottom,
      [data-theme='dark'] .border-end,
      [data-theme='dark'] .border-start { border-color: var(--border-color) !important; }

      [data-theme='dark'] ::-webkit-scrollbar { width: 12px; }
      [data-theme='dark'] ::-webkit-scrollbar-track { background: var(--bg-main); }
      [data-theme='dark'] ::-webkit-scrollbar-thumb {
          background-color: var(--border-color);
          border-radius: 20px;
          border: 3px solid var(--bg-main);
      }

      /* ── Page title strip ── */
  .page-title-strip {
    background-color: #FAFAFA !important;   /* ✅ your desired color */
    border: none !important;
    box-shadow: none !important;

    padding: 14px 24px 10px 24px;  /* clean spacing */
    margin-top: 0px;
  }
    .page-title-strip h6 {
    font-size: 1.2rem !important;
    font-weight: 600;
    margin-bottom: 2px;
  }

  .page-title-strip small {
    font-size: 0.85rem !important;
    color: #6b7280 !important;
  }
    .page-subtitle {
    color: #6b7280;
    font-size: 0.85rem;
  }
    `;

    // ✅ Identify if we are on the Chat Interface
    const isChatInterface = currentPath === "/chatinterface" || currentPath.startsWith("/chatinterface/");

    return (
      <div className="d-flex flex-column vh-100 app-theme-bg">
        <style>{themeStyles}</style>

        {/* ── TOP NAVBAR ── */}
        {!shouldHideSidebar && <Sidebar />}

        {/* ✅ Use overflow-hidden for chat so it fills the screen perfectly */}
        <div className={`flex-grow-1 ${isChatInterface ? 'overflow-hidden' : 'overflow-auto'}`}>

          {/* ✅ Hide title strip ONLY on Chat Interface */}
          {!shouldHideSidebar && !isChatInterface && (
            <div className="page-title-strip">
              <h6>{title}</h6>
              <small>{subtitle}</small>
            </div>
          )}

          {children}

        </div>

      </div>
    );
  };

  export default Layout;